import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { createPost } from "../services/postService";
import { Timestamp, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import heic2any from "heic2any";
const DAY_MS = 86_400_000;

interface SocialBlockDetails {
  isBlocked?: boolean;
  blockedDuration?: number | null;
  blockedAt?: { toMillis?: () => number };
}

// Client-computed expiry — mirrors the check used on the Social feed screen.
const isSuspensionActive = (b?: SocialBlockDetails | null): boolean => {
  if (!b || !b.isBlocked) return false;
  if (b.blockedDuration == null) return true;
  const startMs = b.blockedAt?.toMillis?.() ?? 0;
  return startMs + b.blockedDuration * DAY_MS > Date.now();
};
interface CreatePostDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  username: string;
  userImageUrl: string;
  trainingLogData: {
    trainingLogId: string;
    sessionId?: string;
    notes: string;
    duration: number;
    load?: number;
    averageHR?: number;
    activityLogId?: string;
    performanceLogId?: string;
  };
}

const CreatePostDialog: React.FC<CreatePostDialogProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  userImageUrl,
  trainingLogData,
}) => {
  const navigate = useNavigate();
  const [socialBlock, setSocialBlock] = useState<SocialBlockDetails | null>(null);

  // Fetched fresh each time the dialog opens rather than relying on cached
  // AuthContext data, since socialBlockDetails lives on a separate doc
  // (users/{uid}/private/socialBlockDetails) that isn't part of that cache.
  useEffect(() => {
    if (!isOpen || !userId) return;
    let cancelled = false;
    getDoc(doc(db, "users", userId, "private", "socialBlockDetails"))
      .then((snap) => {
        if (cancelled) return;
        setSocialBlock((snap.data() as SocialBlockDetails | undefined) ?? null);
      })
      .catch((e) => {
        console.error("Error fetching suspension status:", e);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  const isSuspended = isSuspensionActive(socialBlock);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPostPrivate, setIsPostPrivate] = useState(false);
  const [isGPSPrivate, setIsGPSPrivate] = useState(false);
  const [editableNotes, setEditableNotes] = useState(
    trainingLogData.notes || "",
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image repositioning state
  const [imagePosition, setImagePosition] = useState({ x: 50, y: 50 });
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0,
    posX: 50,
    posY: 50,
  });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageNaturalSize = useRef({ width: 0, height: 0 });

  const isHeicFile = (file: File): boolean => {
    const name = file.name.toLowerCase();
    return (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      name.endsWith(".heic") ||
      name.endsWith(".heif")
    );
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (allow image/* and HEIC/HEIF)
      if (!file.type.startsWith("image/") && !isHeicFile(file)) {
        setError("Please select an image file");
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("Image size must be less than 10MB");
        return;
      }

      setError(null);

      // Convert HEIC/HEIF to JPEG
      if (isHeicFile(file)) {
        try {
          const convertedBlob = (await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.85,
          })) as Blob;

          const convertedFile = new File(
            [convertedBlob],
            file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"),
            { type: "image/jpeg" },
          );

          setImageFile(convertedFile);
          setImagePreview(URL.createObjectURL(convertedBlob));
        } catch (err) {
          console.error("HEIC conversion failed:", err);
          setError("Failed to convert HEIC image. Please try a JPG or PNG.");
        }
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImagePosition({ x: 50, y: 50 });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    imageNaturalSize.current = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    };
  };

  const getClientPos = (e: React.MouseEvent | React.TouchEvent) => {
    if ("touches" in e)
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getClientPos(e);
    setIsDraggingImage(true);
    setDragStart({
      x: pos.x,
      y: pos.y,
      posX: imagePosition.x,
      posY: imagePosition.y,
    });
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDraggingImage || !imageContainerRef.current) return;
    e.preventDefault();
    const pos = getClientPos(e);
    const container = imageContainerRef.current;
    const rect = container.getBoundingClientRect();
    const natural = imageNaturalSize.current;
    if (!natural.width || !natural.height) return;
    const containerAspect = rect.width / rect.height;
    const imageAspect = natural.width / natural.height;
    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;
    const sensitivity = 0.15;
    if (imageAspect > containerAspect) {
      const pctDelta = (dx / rect.width) * 100 * sensitivity;
      setImagePosition((prev) => ({
        ...prev,
        x: Math.max(0, Math.min(100, dragStart.posX - pctDelta)),
      }));
    } else {
      const pctDelta = (dy / rect.height) * 100 * sensitivity;
      setImagePosition((prev) => ({
        ...prev,
        y: Math.max(0, Math.min(100, dragStart.posY - pctDelta)),
      }));
    }
  };

  const handleDragEnd = () => {
    setIsDraggingImage(false);
  };

  const handleCreatePost = async () => {
    // Allow posting without image - no validation required

    try {
      setIsUploading(true);
      setError(null);
      if (isSuspended) {
        setError("Your access to social features is currently suspended.");
        return;
      }

      await createPost(
        userId,
        {
          userID: userId,
          username: username,
          userImageUrl: userImageUrl,
          postTimestamp: Timestamp.now(),
          notes: editableNotes, // Use edited notes instead of original
          duration: trainingLogData.duration,
          mediaList: [], // Will be set by createPost function
          load: trainingLogData.load,
          AverageHR: trainingLogData.averageHR,
          trainingLogID: trainingLogData.trainingLogId,
          sessionID: trainingLogData.sessionId,
          activityLogID: trainingLogData.activityLogId,
          performanceLogID: trainingLogData.performanceLogId,
          ...(imageFile ? { imagePosition } : {}),
        },
        imageFile || undefined, // Convert null to undefined for optional parameter
        !isPostPrivate, // Make training log public when post is public
        isPostPrivate, // Post privacy: false = public, true = private
        isGPSPrivate, // GPS privacy: false = public, true = private
      );

      // Success - close dialog and navigate to Connections page
      onClose();

      // Reset state
      setImageFile(null);
      setImagePreview(null);
      setImagePosition({ x: 50, y: 50 });
      setIsPostPrivate(false);
      setIsGPSPrivate(false);
      setEditableNotes(trainingLogData.notes || ""); // Reset notes to original
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Navigate to Connections page to see the new post
      navigate("/connections");
    } catch (err) {
      console.error("Error creating post:", err);
      setError("Failed to create post. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50 sm:p-4">
      <div className="bg-base-100 sm:rounded-xl max-w-2xl w-full h-full sm:h-auto sm:max-h-[70vh] flex flex-col sm:block sm:overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 px-8 pb-0  py-4 pt-8 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-base-content">
            Create Post
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-base-200 transition-colors"
            disabled={isUploading}
          >
            <XMarkIcon className="h-5 w-5 text-base-content/80" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto sm:flex-none">
          <div className="p-8 pt-0">
            {/* Training Info Preview */}
            <div className="bg-base-200-6">
              <div className="mt-4 mb-2">
                <textarea
                  value={editableNotes}
                  onChange={(e) => setEditableNotes(e.target.value)}
                  placeholder="Add your training notes here..."
                  className="w-full p-4 bg-base-200 text-[16px] sm:text-sm h-36 resize-none focus:outline-none focus:ring-0 rounded-md text-base-content"
                  style={{ lineHeight: "1.1" }}
                  disabled={isUploading}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center bg-base-200 rounded-md p-4">
                  <p className="text-base-content/60 font-inter text-sm">
                    Duration
                  </p>
                  <p className="ml-2  text-base-content font-bold text-xl">
                    {Math.floor(trainingLogData.duration / 60)}h{" "}
                    {Math.round(trainingLogData.duration % 60)}m
                  </p>
                </div>
                {trainingLogData.load && (
                  <div className="text-center bg-base-200 rounded-md p-4">
                    <p className="text-base-content/60 font-inter text-sm">
                      Load
                    </p>
                    <p className="ml-2  text-base-content font-bold text-xl">
                      {Math.round(trainingLogData.load)}
                    </p>
                  </div>
                )}
                {trainingLogData.averageHR && (
                  <div className="text-center bg-base-200 rounded-md p-4">
                    <p className="text-base-content/60 font-inter text-sm">
                      Avg HR
                    </p>
                    <p className="ml-2 font-medium text-base-content font-heading text-xl">
                      {Math.round(trainingLogData.averageHR)} bpm
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="mb-6 mt-6">
              {!imagePreview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-base-300 rounded-lg p-8 text-center cursor-pointer hover:border-base-300 transition-colors"
                >
                  <PhotoIcon className="h-12 w-12 mx-auto text-base-content/50 mb-3" />
                  <p className="text-sm text-base-content/80 mb-1">
                    Click to upload a photo (optional)
                  </p>
                  <p className="text-xs text-base-content/60">
                    PNG, JPG, HEIC up to 10MB
                  </p>
                </div>
              ) : (
                <div
                  ref={imageContainerRef}
                  className="relative w-full h-80 md:h-96 rounded-md overflow-hidden select-none"
                  style={{
                    cursor: isDraggingImage ? "grabbing" : "grab",
                    touchAction: "none",
                  }}
                  onMouseDown={handleDragStart}
                  onMouseMove={handleDragMove}
                  onMouseUp={handleDragEnd}
                  onMouseLeave={handleDragEnd}
                  onTouchStart={handleDragStart}
                  onTouchMove={handleDragMove}
                  onTouchEnd={handleDragEnd}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover pointer-events-none"
                    style={{
                      objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                    }}
                    onLoad={handleImageLoad}
                    draggable={false}
                  />
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full">
                    <span className="text-xs text-white font-grotesk">
                      Drag to reposition
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    disabled={isUploading}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>

            {/* Privacy Controls Section */}
            {(trainingLogData.trainingLogId ||
              trainingLogData.activityLogId ||
              trainingLogData.performanceLogId) && (
              <div className="mb-6 space-y-3">
                {/* POST PRIVACY TOGGLE */}
                <div
                  onClick={() => setIsPostPrivate(!isPostPrivate)}
                  className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${
                    isPostPrivate ? "bg-base-200" : "bg-base-200"
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-base-content">
                      Private Post?
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${isPostPrivate ? "text-blue-500 font-bold" : "text-base-content/60"}`}
                    >
                      {isPostPrivate
                        ? "Only visible to you"
                        : "Shared with the community"}
                    </p>
                  </div>
                  <div
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      isPostPrivate ? "bg-primary" : "bg-base-300"
                    }`}
                  >
                    <div
                      className={`absolute top-[2px] h-5 w-5 rounded-full bg-white transition-transform ${
                        isPostPrivate ? "translate-x-[22px]" : "left-[2px]"
                      }`}
                    />
                  </div>
                </div>

                {/* GPS PRIVACY TOGGLE */}
                <div
                  onClick={() => setIsGPSPrivate(!isGPSPrivate)}
                  className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${
                    isGPSPrivate ? "bg-base-200" : "bg-base-200"
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-base-content">
                      Hide GPS Route?
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${isGPSPrivate ? "text-blue-500 font-bold" : "text-base-content/60"}`}
                    >
                      {isGPSPrivate
                        ? "Route data hidden from others"
                        : "Route data visible to others"}
                    </p>
                  </div>
                  <div
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      isGPSPrivate ? "bg-primary" : "bg-base-300"
                    }`}
                  >
                    <div
                      className={`absolute top-[2px] h-5 w-5 rounded-full bg-white transition-transform ${
                        isGPSPrivate ? "translate-x-[22px]" : "left-[2px]"
                      }`}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions - Fixed at bottom on mobile, inline on desktop */}
        <div className="sticky bottom-0 sm:static bg-base-100 px-8 sm:p-8 sm:pt-0 pb-12 sm:pm-0">
          <button
            onClick={handleCreatePost}
            disabled={isUploading || isSuspended}
            className="w-full py-3 bg-primary text-white rounded-xl font-grotesk font-bold hover:bg-primary/90 disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            {isUploading ? (
              <>
                <div className="loading loading-spinner loading-sm"></div>
                <span>Creating Post...</span>
              </>
            ) : (
              <span>Create Post</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePostDialog;
